export type ProductCategory = 'cabinet' | 'tablet' | 'hardware' | 'app';

export interface ProductAddOn {
  id: string;
  name: string;
  priceUsd: number;
  description: string;
}

export interface ProductSpec {
  label: string;
  value: string;
}

export interface Product {
  id: string;
  name: string;
  category: ProductCategory;
  tagline: string;
  description: string;
  priceUsd: number;
  /** Shown next to the price, e.g. shipping caveats. */
  priceNote: string;
  /** Optional — software / bundled items can omit a photo. */
  image?: string;
  imageAlt?: string;
  highlights: string[];
  specs: ProductSpec[];
  addOns: ProductAddOn[];
  /** Rendered larger / first on pricing grids. */
  featured?: boolean;
}

export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  cabinet: 'Metal cabinets',
  tablet: 'Tablet LaunchPads',
  hardware: 'Computer boards',
  app: 'Software',
};

/** Re-imaging coverage for the computer board inside any NEMO hardware (KAN-852). */
export const EXTENDED_WARRANTY_ADD_ON: ProductAddOn = {
  id: 'extended-warranty',
  name: 'Extended Warranty Protection',
  priceUsd: 300,
  description: 'FREE unlimited re-imaging between supported platforms, tracked by board serial number.',
};

export const PRODUCTS: Product[] = [
  {
    id: 'street-cabinet',
    name: 'NEMO Street Cabinet',
    category: 'cabinet',
    tagline: 'Big impact. Compact size. Real results.',
    description:
      'A 21.5" touch-screen floor cabinet built for high-traffic venues that want powerful exposure, with a footprint that fits almost anywhere.',
    priceUsd: 2500,
    priceNote: 'plus shipping (quoted separately)',
    image: '/images/nemo-street-cabinet.jpg',
    imageAlt: 'NEMO Street Cabinet, a slim 21.5 inch touch-screen advertising cabinet',
    highlights: [
      'Crisp, responsive 21.5" touch screen for engaging ads and promotions',
      'Durable metal build engineered for 24/7 operation',
      'Wi-Fi and LAN connectivity for real-time content delivery',
      'Locking cabinet with secure access to protect your investment',
      'Sleek footprint — maximum impact, minimal space',
    ],
    specs: [
      { label: 'Display', value: '21.5" capacitive touch screen' },
      { label: 'Build', value: 'Powder-coated steel, locking door' },
      { label: 'Connectivity', value: 'Wi-Fi + LAN (RJ45)' },
      { label: 'Placement', value: 'Free-standing floor cabinet' },
      { label: 'Setup', value: 'Configured and installed free of charge' },
    ],
    addOns: [EXTENDED_WARRANTY_ADD_ON],
    featured: true,
  },
  {
    id: 'boomer-cabinet',
    name: 'NEMO Boomer Cabinet',
    category: 'cabinet',
    tagline: 'Advertise. Engage. Monetize.',
    description:
      'Our flagship 43-inch cabinet. The largest, most vibrant display in the range — built for venues where you want the screen to be the first thing every customer sees.',
    priceUsd: 3500,
    priceNote: 'plus shipping (quoted separately)',
    image: '/images/nemo-boomer-cabinet.jpg',
    imageAlt: 'NEMO Boomer Cabinet, a 43 inch free-standing advertising kiosk',
    highlights: [
      '43-inch monitor — large, vibrant display for maximum impact',
      'Durable metal build for high-traffic environments',
      'Wi-Fi and LAN for real-time content updates',
      'Lockable door for full hardware protection',
      'Vinyl wrap ready — customise the cabinet to your brand',
    ],
    specs: [
      { label: 'Display', value: '43" commercial monitor' },
      { label: 'Build', value: 'Heavy-duty steel, lockable service door' },
      { label: 'Connectivity', value: 'Wi-Fi + LAN (RJ45)' },
      { label: 'Placement', value: 'Free-standing floor cabinet' },
      { label: 'Setup', value: 'Configured and installed free of charge' },
    ],
    addOns: [
      {
        id: 'vinyl-wrap',
        name: 'Custom vinyl wrap',
        priceUsd: 350,
        description: 'Full-cabinet branded wrap in your artwork or one of our designs.',
      },
      EXTENDED_WARRANTY_ADD_ON,
    ],
  },
  {
    id: 'launchpad-mini',
    name: 'NEMO LaunchPad Mini',
    category: 'tablet',
    tagline: 'Countertop display',
    description:
      'A commercial Android tablet on a weighted countertop stand — the fastest, cheapest way to put a signup screen right next to your register.',
    priceUsd: 600,
    priceNote: 'plus shipping',
    image: '/images/launchpad-tablet-in-store.png',
    imageAlt: 'A NEMO LaunchPad tablet mounted on a countertop stand at a convenience store register',
    highlights: [
      'Commercial Android tablet, preconfigured and ready for immediate deployment',
      'Premium weighted countertop stand with security lock',
      'Runs the LaunchPad marketing app out of the box',
      'Your venue settings are configured before it ships',
    ],
    specs: [
      { label: 'Device', value: 'Commercial-grade Android tablet' },
      { label: 'Mount', value: 'Weighted countertop stand' },
      { label: 'Connectivity', value: 'Wi-Fi' },
      { label: 'Software', value: 'LaunchPad marketing app (included free)' },
    ],
    addOns: [],
    featured: true,
  },
  {
    id: 'launchpad-pro',
    name: 'NEMO LaunchPad Pro',
    category: 'tablet',
    tagline: 'Floor-standing display',
    description:
      'The same commercial tablet and software on a full-height floor stand, for lobbies, entrances and waiting areas where a countertop is not an option.',
    priceUsd: 650,
    priceNote: 'plus shipping',
    image: '/images/nemo-street-cabinet.jpg',
    imageAlt: 'NEMO LaunchPad Pro floor-standing tablet display',
    highlights: [
      'Commercial Android tablet, preconfigured and ready for immediate deployment',
      'Floor-standing stand with a stable weighted base',
      'Runs the LaunchPad marketing app out of the box',
      'Your venue settings are configured before it ships',
    ],
    specs: [
      { label: 'Device', value: 'Commercial-grade Android tablet' },
      { label: 'Mount', value: 'Floor-standing stand, weighted base' },
      { label: 'Connectivity', value: 'Wi-Fi' },
      { label: 'Software', value: 'LaunchPad marketing app (included free)' },
    ],
    addOns: [],
  },
  {
    id: 'computer-board',
    name: 'NEMO LaunchPad Computer Board',
    category: 'hardware',
    tagline: 'One computer. Multiple platforms.',
    description:
      'Our NEMO LaunchPad computer boards are designed for cross-platform compatibility, allowing customers to switch between supported gaming software platforms without purchasing new hardware.',
    priceUsd: 500,
    priceNote: 'each, plus shipping — $800 with Extended Warranty',
    highlights: [
      'Cross-platform compatibility on one board',
      'Registered and tracked by serial number',
      'Add Extended Warranty Protection for $300 — free unlimited re-imaging',
    ],
    specs: [
      { label: 'Computer Board', value: '$500 each' },
      { label: 'Extended Warranty Protection', value: '$300 per board' },
      { label: 'Total Package', value: '$800 per board' },
    ],
    addOns: [EXTENDED_WARRANTY_ADD_ON],
  },
  {
    id: 'launchpad-app',
    name: 'LaunchPad Marketing App',
    category: 'app',
    tagline: 'Always included. Always free.',
    description:
      'The software that makes the hardware pay for itself. Customers tap the screen, enter their phone number, and instantly receive an SMS with your signup link and promotional offers.',
    priceUsd: 0,
    priceNote: 'included with every LaunchPad and cabinet',
    highlights: [
      'Tap-to-signup flow: phone number in, SMS out — no app download for the customer',
      'Your signup link and promo offer sent automatically',
      'Remote content updates — change the ad without visiting the venue',
      'Live device monitoring, screenshots and remote restart',
    ],
    specs: [
      { label: 'Price', value: 'Free, for the life of the device' },
      { label: 'Platforms', value: 'Android tablets and NEMO cabinets' },
      { label: 'Management', value: 'Web dashboard login' },
    ],
    addOns: [],
  },
];

export const PRODUCT_INDEX: ReadonlyMap<string, Product> = new Map(PRODUCTS.map((product) => [product.id, product]));

export function getProduct(id: string): Product | undefined {
  return PRODUCT_INDEX.get(id);
}

export function getProductsByCategory(category: ProductCategory): Product[] {
  return PRODUCTS.filter((product) => product.category === category);
}

/** Products a customer can actually order (the app is bundled, not sold). */
export const PURCHASABLE_PRODUCTS: Product[] = PRODUCTS.filter((product) => product.category !== 'app');
